import React, { useState } from 'react';
import {
  CheckCheck, MessageCircle, Trash2, Edit2, Reply,
  Smile, MoreVertical, Music, FileText, Film
} from 'lucide-react';
import { format } from 'date-fns';

interface MessageItemProps {
  id: string;
  content: string;
  sender_id: string;
  current_user_id: string;
  sender_name: string;
  sender_avatar?: string;
  created_at: string;
  is_read?: boolean;
  media_url?: string;
  media_type?: 'image' | 'video' | 'audio' | 'document';
  is_system_message?: boolean;
  onDelete?: (messageId: string) => void;
  onReply?: (messageId: string) => void;
  onEdit?: (messageId: string) => void;
  showSenderInfo?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({
  id,
  content,
  sender_id,
  current_user_id,
  sender_name,
  sender_avatar,
  created_at,
  is_read = false,
  media_url,
  media_type,
  is_system_message = false,
  onDelete,
  onReply,
  onEdit,
  showSenderInfo = true
}) => {
  const isOwnMessage = sender_id === current_user_id;
  const [showActions, setShowActions] = useState(false);

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'HH:mm');
    } catch {
      return '--:--';
    }
  };

  if (is_system_message) {
    return (
      <div className="flex justify-center my-4">
        <div className="px-3 py-1 bg-light-border dark:bg-dark-border rounded-full">
          <p className="text-xs text-light-subtle dark:text-dark-subtle text-center">
            {content}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2 mb-4 group ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Avatar */}
      {!isOwnMessage && showSenderInfo && (
        <div className="w-8 h-8 rounded-full flex-shrink-0 bg-gradient-to-br from-primary/20 to-golden/20 dark:from-primary/30 dark:to-golden/30 flex items-center justify-center overflow-hidden text-xs font-semibold text-primary dark:text-golden">
          {sender_avatar ? (
            <img src={sender_avatar} alt={sender_name} className="w-full h-full object-cover" />
          ) : (
            sender_name.charAt(0).toUpperCase()
          )}
        </div>
      )}
      {isOwnMessage && <div className="w-8" />}

      {/* Message Content */}
      <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[65%]`}>
        {!isOwnMessage && showSenderInfo && (
          <span className="text-xs font-semibold text-light-text dark:text-dark-text mb-1">
            {sender_name}
          </span>
        )}

        <div
          className={`rounded-xl px-4 py-2.5 break-words ${
            isOwnMessage
              ? 'bg-primary dark:bg-golden text-white rounded-br-none'
              : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-bl-none'
          }`}
          role="article"
          aria-label={`Message from ${sender_name}: ${content}`}
        >
          {media_url && (
            <div className="mb-2">
              {media_type === 'image' && (
                <img
                  src={media_url}
                  alt="Shared image"
                  className="max-w-full max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition"
                />
              )}
              {media_type === 'video' && (
                <div className="relative">
                  <video
                    src={media_url}
                    controls
                    className="max-w-full max-h-64 rounded-lg"
                  />
                  <Film className="absolute top-2 right-2 h-5 w-5 text-white/80" />
                </div>
              )}
              {media_type === 'audio' && (
                <div className="flex items-center gap-2 p-2 bg-dark/10 dark:bg-white/10 rounded-lg">
                  <Music className="h-4 w-4" />
                  <audio src={media_url} controls className="flex-1 h-6" />
                </div>
              )}
              {media_type === 'document' && (
                <a
                  href={media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 bg-dark/10 dark:bg-white/10 rounded-lg hover:opacity-80 transition"
                >
                  <FileText className="h-4 w-4" />
                  <span className="text-sm truncate">Document</span>
                </a>
              )}
            </div>
          )}

          <p className={`whitespace-pre-wrap text-sm ${
            isOwnMessage ? 'text-white' : 'text-light-text dark:text-dark-text'
          }`}>
            {content}
          </p>
        </div>

        {/* Time and Status */}
        <div className={`flex items-center gap-1 mt-1 text-xs ${
          isOwnMessage
            ? 'text-light-subtle dark:text-dark-subtle'
            : 'text-light-subtle dark:text-dark-subtle'
        }`}>
          <span>{formatTime(created_at)}</span>
          {isOwnMessage && (
            <CheckCheck className={`h-3 w-3 ${is_read ? 'text-primary dark:text-golden' : 'text-current'}`} />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {showActions && !is_system_message && (
        <div className={`flex items-center gap-1 ${isOwnMessage ? 'mr-1' : 'ml-1'}`}>
          <button
            onClick={() => onReply?.(id)}
            className="p-1.5 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition opacity-0 group-hover:opacity-100"
            title="Reply"
          >
            <Reply className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
          </button>
          <button
            onClick={() => onEdit?.(id)}
            className="p-1.5 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition opacity-0 group-hover:opacity-100"
            title="Edit"
          >
            <Edit2 className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
          </button>
          <button
            onClick={() => onDelete?.(id)}
            className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition opacity-0 group-hover:opacity-100"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageItem;
